import React, { useState } from "react";
import {
  Table,
  Space,
  Button,
  Tag,
  Avatar,
  Typography,
  Card,
  Row,
  Col,
  Select,
  Input,
  Dropdown,
  MenuProps,
  Tooltip,
  Rate,
} from "antd";
import { useList, useDelete, useNavigation } from "@refinedev/core";
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  StarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Actor } from "@/types";

const { Title, Text } = Typography;
const { Option } = Select;

export const ArtistList: React.FC = () => {
  const [searchValue, setSearchValue] = useState("");

  const { mutate: deleteActor } = useDelete();
  const { push } = useNavigation();

  const {
    data: actorsData,
    isLoading,
    refetch,
  } = useList<Actor>({
    resource: "actors",
    pagination: { current: 1, pageSize: 20 },
    filters: [{ field: "search", operator: "contains", value: searchValue }],
  });

  const handleDelete = (id: number) => {
    deleteActor({
      resource: "actors",
      id,
      successNotification: () => ({
        message: "Artiste supprimé",
        description: "L'artiste a été supprimé avec succès",
        type: "success",
      }),
    });
  };

  const getRoleColor = (profession?: string) => {
    switch (profession?.toLowerCase()) {
      case "actor":
      case "acteur":
        return "purple";
      case "actress":
      case "actrice":
        return "pink";
      case "director":
      case "réalisateur":
      case "réalisatrice":
        return "blue";
      case "producer":
      case "producteur":
        return "green";
      default:
        return "default";
    }
  };

  const getActionMenu = (record: Actor): MenuProps["items"] => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "Voir",
      onClick: () => push(`/artists/${record.id}`),
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Modifier",
      onClick: () => push(`/artists/edit/${record.id}`),
    },
    {
      type: "divider",
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Supprimer",
      danger: true,
      onClick: () => handleDelete(record.id),
    },
  ];

  const columns = [
    {
      title: "",
      dataIndex: "checkbox",
      key: "checkbox",
      width: 50,
      render: () => <input type="checkbox" />,
    },
    {
      title: "PROFIL",
      dataIndex: "profile",
      key: "profile",
      render: (_: any, record: Actor) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            size={60}
            src={record.profileImageUrl}
            style={{ borderRadius: 8 }}
          >
            {record.firstName?.charAt(0)}
            {record.lastName?.charAt(0)}
          </Avatar>
          <div>
            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
              {record.fullName}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.profession} • {record.movieCount || 0} films
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "NOTE & VUES",
      dataIndex: "rating",
      key: "rating",
      render: (_: any, record: Actor) => (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginBottom: 4,
            }}
          >
            <StarOutlined style={{ color: "#faad14" }} />
            <Text strong>{record.rating?.toFixed(1) || "4.8"}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.viewCount
              ? `${(record.viewCount / 1000).toFixed(0)}K vues`
              : "23K vues"}
          </Text>
        </div>
      ),
    },
    {
      title: "GENRES",
      dataIndex: "genres",
      key: "genres",
      render: (_: any, record: Actor) => (
        <Space wrap>
          <Tag color="default">Drame</Tag>
          <Tag color="default">Thriller</Tag>
          <Tag color="default">Action</Tag>
        </Space>
      ),
    },
    {
      title: "ACTIONS",
      key: "actions",
      render: (_: any, record: Actor) => (
        <Space>
          <Tooltip title="Voir les détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => push(`/artists/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => push(`/artists/edit/${record.id}`)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getActionMenu(record) }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          Gestion des Artistes et Réalisateurs
        </Title>
        <Text type="secondary">
          Gérez les profils et les associations aux contenus
        </Text>
      </div>

      {/* Action Buttons */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Space>
            <Button icon={<PlusOutlined />}>+ Ajouter un profil</Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<SearchOutlined />}>Exporter</Button>
          </Space>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Rechercher un artiste ou réalisateur..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ maxWidth: 400 }}
            />
          </Col>
          <Col>
            <Space>
              <Select placeholder="Type" style={{ width: 120 }} allowClear>
                <Option value="actor">Acteur</Option>
                <Option value="actress">Actrice</Option>
                <Option value="director">Réalisateur</Option>
                <Option value="producer">Producteur</Option>
              </Select>
              <Select placeholder="Genre" style={{ width: 120 }} allowClear>
                <Option value="drama">Drame</Option>
                <Option value="action">Action</Option>
                <Option value="comedy">Comédie</Option>
                <Option value="thriller">Thriller</Option>
              </Select>
              <Select
                placeholder="Popularité"
                style={{ width: 120 }}
                allowClear
              >
                <Option value="high">Élevée</Option>
                <Option value="medium">Moyenne</Option>
                <Option value="low">Faible</Option>
              </Select>
              <Select placeholder="Statut" style={{ width: 120 }} allowClear>
                <Option value="active">Actif</Option>
                <Option value="inactive">Inactif</Option>
              </Select>
              <Button type="primary" icon={<SearchOutlined />}>
                Filtrer
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Artists Grid */}
      <Row gutter={[16, 16]}>
        {actorsData?.data?.map((actor: Actor) => (
          <Col xs={24} sm={12} lg={8} xl={6} key={actor.id}>
            <Card
              hoverable
              cover={
                <div
                  style={{
                    position: "relative",
                    height: 200,
                    background: "#f0f0f0",
                  }}
                >
                  <Avatar
                    size={80}
                    src={actor.profileImageUrl}
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    {actor.firstName?.charAt(0)}
                    {actor.lastName?.charAt(0)}
                  </Avatar>
                  <Tag
                    color={getRoleColor(actor.profession)}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                    }}
                  >
                    {actor.profession || "Acteur"}
                  </Tag>
                </div>
              }
              actions={[
                <Tooltip title="Modifier" key="edit">
                  <EditOutlined
                    onClick={() => push(`/artists/edit/${actor.id}`)}
                  />
                </Tooltip>,
                <Tooltip title="Lien" key="link">
                  <UserOutlined />
                </Tooltip>,
              ]}
            >
              <Card.Meta
                title={actor.fullName}
                description={
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      {actor.profession} • {actor.movieCount || 15} films
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        marginBottom: 8,
                      }}
                    >
                      <StarOutlined
                        style={{ color: "#faad14", fontSize: 14 }}
                      />
                      <Text strong style={{ fontSize: 14 }}>
                        {actor.rating?.toFixed(1) || "4.8"}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {actor.viewCount
                          ? `${(actor.viewCount / 1000).toFixed(0)}K vues`
                          : "23K vues"}
                      </Text>
                    </div>
                    <Space wrap size="small">
                      <Tag color="default" style={{ fontSize: 10 }}>
                        Drame
                      </Tag>
                      <Tag color="default" style={{ fontSize: 10 }}>
                        Thriller
                      </Tag>
                      <Tag color="default" style={{ fontSize: 10 }}>
                        Action
                      </Tag>
                    </Space>
                    <div style={{ marginTop: 12 }}>
                      <Button type="primary" size="small" block>
                        Modifier
                      </Button>
                    </div>
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Table View (Alternative) */}
      <Card style={{ marginTop: 24 }}>
        <Table
          columns={columns}
          dataSource={actorsData?.data}
          loading={isLoading}
          rowKey="id"
          pagination={{
            total: actorsData?.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} artistes`,
          }}
        />
      </Card>
    </div>
  );
};
